import * as FilePond from 'filepond';
import ApplicationController from './application_controller';

/**
 * Generic Image Upload Controller using FilePond and S3 Direct Upload
 *
 * This controller handles image uploads directly to S3 using presigned URLs,
 * bypassing the Django server for the actual file transfer.
 *
 * Required data attributes:
 * - data-image-upload-api-url-value: URL to request presigned upload URL
 * - data-image-upload-upload-type-value: Type of upload (e.g., "profile_photo", "experience_photo")
 *
 * Optional data attributes:
 * - data-image-upload-max-file-size-value: Max file size in MB (default: 10)
 * - data-image-upload-max-files-value: Max number of files (default: 1)
 * - data-image-upload-accepted-types-value: Comma-separated MIME types
 * - data-image-upload-csrf-token-value: CSRF token for API requests
 *
 * Events dispatched:
 * - image-upload:success - { s3_key, file }
 * - image-upload:error - { error, file }
 * - image-upload:complete - { s3_keys[] }
 *
 * Usage example in template:
 * <div data-controller="image-upload"
 *      data-image-upload-api-url-value="/api/v1/content/request-image-upload-url/"
 *      data-image-upload-upload-type-value="profile_photo"
 *      data-image-upload-csrf-token-value="{{ csrf_token }}"
 *      data-image-upload-max-file-size-value="5">
 *   <input type="file"
 *          data-image-upload-target="input"
 *          accept="image/*">
 * </div>
 */
export default class extends ApplicationController {
  static targets = ['input'];

  static values = {
    apiUrl: String,
    uploadType: String,
    maxFileSize: { type: Number, default: 10 },
    maxFiles: { type: Number, default: 1 },
    acceptedTypes: { type: String, default: 'image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp' },
    csrfToken: String,
  };

  connect() {
    if (!this.hasInputTarget) {
      console.error('ImageUploadController: No input target found');
      return;
    }

    if (!this.apiUrlValue) {
      console.error('ImageUploadController: No API URL provided');
      return;
    }

    this.uploadedKeys = [];
    this.initFilePond();
  }

  disconnect() {
    if (this.pond) {
      this.pond.destroy();
    }
  }

  initFilePond() {
    const acceptedFileTypes = this.acceptedTypesValue.split(',').map((type) => type.trim());

    this.pond = FilePond.create(this.inputTarget, {
      maxFiles: this.maxFilesValue,
      maxFileSize: `${this.maxFileSizeValue}MB`,
      acceptedFileTypes,
      allowImagePreview: true,
      imagePreviewHeight: 170,
      credits: false,

      // Server configuration for direct S3 upload
      server: {
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          this.processUpload(file, load, error, progress, abort);
        },
      },

      // Labels
      labelIdle: 'Drag & Drop your image or <span class="filepond--label-action">Browse</span>',
      labelFileProcessing: 'Uploading',
      labelFileProcessingComplete: 'Upload complete',
      labelFileProcessingError: 'Error during upload',
      labelTapToCancel: 'tap to cancel',
      labelTapToRetry: 'tap to retry',
    });
  }

  async processUpload(file, load, error, progress, abort) {
    try {
      // Step 1: Request presigned URL from Django
      const presignedData = await this.requestPresignedUrl(file);

      // Step 2: Upload directly to S3
      await this.uploadToS3(
        file,
        presignedData,
        (progressEvent) => {
          // Update FilePond progress
          progress(progressEvent.lengthComputable, progressEvent.loaded, progressEvent.total);
        },
      );

      // Store the S3 key
      this.uploadedKeys.push(presignedData.s3_key);

      // Dispatch success event
      this.dispatch('success', {
        detail: {
          s3_key: presignedData.s3_key,
          file: file.name,
        },
      });

      // FilePond success callback
      load(presignedData.s3_key);
    } catch (err) {
      console.error('Upload failed:', err);

      // Dispatch error event
      this.dispatch('error', {
        detail: {
          error: err.message || 'Upload failed',
          file: file.name,
        },
      });

      // FilePond error callback
      error(err.message || 'Upload failed');
    }
  }

  async requestPresignedUrl(file) {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add CSRF token if provided
    if (this.csrfTokenValue) {
      headers['X-CSRFToken'] = this.csrfTokenValue;
    }

    const response = await fetch(this.apiUrlValue, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        upload_type: this.uploadTypeValue,
        max_file_size_mb: this.maxFileSizeValue,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to get upload URL' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async uploadToS3(file, presignedData, onProgress) {
    return new Promise((resolve, reject) => {
      const formData = new FormData();

      // Add presigned fields first (order matters for S3)
      Object.entries(presignedData.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Add file last
      formData.append('file', file);

      // Create XHR for progress tracking
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', onProgress);

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('POST', presignedData.upload_url);
      xhr.send(formData);
    });
  }

  /**
   * Get all uploaded S3 keys
   * Can be called from other controllers or scripts
   */
  getUploadedKeys() {
    return [...this.uploadedKeys];
  }

  /**
   * Clear uploaded keys (useful for form resets)
   */
  clearUploadedKeys() {
    this.uploadedKeys = [];
  }
}
